import { useEffect, useState } from 'react';
import { AlertCircle, CalendarX, RefreshCcw, ExternalLink, Wallet, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { TabsContent } from '../../../ui/tabs';
import { paymentsApi, type PaymentTransaction, type PaymentTransactionStatus } from '../../../../lib/api/payments';
import { creditsApi, CREDIT_SOURCE_LABEL, type CustomerCreditEntry } from '../../../../lib/api/credits';
import { GrantCreditModal } from '../GrantCreditModal';

interface Props {
  visible: boolean;
  wpUserId: number;
  embedded?: boolean;
  /** Öffnet den Kündigungs-Assistenten für diesen Vertrag; fehlt sie, entfällt der Knopf. */
  onTerminate?: (enrollmentId: number, label: string) => void;
}

// Verträge, an denen nichts mehr zu kündigen ist.
const BEENDETE_STATUS = new Set(['cancelled', 'canceled', 'ended', 'terminated', 'deleted']);

const STATUS_LABEL: Record<string, string> = {
  succeeded: 'Erfolgreich',
  processing: 'Verarbeitung',
  requires_action: 'Wartet auf Aktion',
  requires_capture: 'Wartet auf Capture',
  requires_payment_method: 'Wartet auf Zahlungsmittel',
  requires_confirmation: 'Wartet auf Bestätigung',
  canceled: 'Storniert',
  failed: 'Fehlgeschlagen',
  refunded: 'Erstattet',
  dispute_open: 'Widerspruch offen',
  dispute_closed: 'Widerspruch geschlossen',
  mandate_revoked: 'SEPA-Mandat widerrufen',
  payment_method_detached: 'Zahlungsmittel entfernt',
  setup_succeeded: 'Setup erfolgreich',
  setup_failed: 'Setup fehlgeschlagen',
};

const STATUS_VARIANT: Record<string, 'default' | 'destructive' | 'secondary' | 'outline'> = {
  succeeded: 'default',
  processing: 'secondary',
  failed: 'destructive',
  refunded: 'secondary',
  dispute_open: 'destructive',
  dispute_closed: 'outline',
  canceled: 'secondary',
  mandate_revoked: 'destructive',
  payment_method_detached: 'destructive',
};

function formatDate(iso: string | null): string {
  if (!iso) return '–';
  return new Date(iso).toLocaleString('de-DE');
}

function formatAmount(cents: number | null, currency?: string | null): string {
  if (cents === null || cents === undefined) return '–';
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: (currency || 'EUR').toUpperCase(),
  }).format(cents / 100);
}

function statusVariant(s: PaymentTransactionStatus) {
  return STATUS_VARIANT[s] ?? 'outline';
}

export default function PaymentHistoryTab({ visible, wpUserId, embedded = false, onTerminate }: Props) {
  const [rows, setRows] = useState<PaymentTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [credits, setCredits] = useState<CustomerCreditEntry[]>([]);
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [grantModal, setGrantModal] = useState<{ enrollmentId: number; label: string } | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const since = new Date();
      since.setDate(since.getDate() - 365);
      const res = await paymentsApi.listTransactions({
        wpUserId,
        since: since.toISOString().slice(0, 10),
        limit: 200,
      });
      setRows(Array.isArray(res?.rows) ? res.rows : []);
    } catch (e) {
      setRows([]);
      setError(e instanceof Error ? e.message : 'Unbekannter Fehler');
    } finally {
      setLoading(false);
    }
  };

  const loadCredits = async () => {
    if (wpUserId <= 0) return;
    setCreditsLoading(true);
    try {
      const res = await creditsApi.listForCustomer(wpUserId);
      setCredits(Array.isArray(res?.items) ? res.items : []);
    } catch {
      setCredits([]);
    } finally {
      setCreditsLoading(false);
    }
  };

  useEffect(() => {
    if (visible && wpUserId > 0) {
      void load();
      void loadCredits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, wpUserId]);

  const handleOpenGrant = (enrollmentId: number, label: string) => {
    setGrantModal({ enrollmentId, label });
  };

  const content = (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-emerald-500" />
              Kontosaldo pro Vertrag
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Guthaben (Coupon, Kulanz, ausgefallener Unterricht …) wird automatisch mit der nächsten passenden Zahlung verrechnet.
            </p>
          </div>
        </CardHeader>
        <CardContent>
          {creditsLoading && (
            <p className="text-sm text-muted-foreground">Wird geladen …</p>
          )}
          {!creditsLoading && credits.length === 0 && (
            <p className="text-sm text-muted-foreground">Keine Verträge für diesen Kunden.</p>
          )}
          {credits.length > 0 && (
            <div className="space-y-3">
              {credits.map((entry) => {
                const label = [
                  entry.instrumentName ?? 'Vertrag',
                  entry.teacherName,
                  entry.standortName,
                ].filter(Boolean).join(' · ');
                const balanceCents = entry.balance?.balanceCents ?? 0;
                const sources = entry.balance?.sources ?? [];
                return (
                  <div key={entry.enrollmentId} className="rounded-md border p-3">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">
                          #{entry.enrollmentId} · {label || 'Unterrichtsvertrag'}
                        </p>
                        <p className="text-xs text-muted-foreground">Schülerprofil {entry.subprofileId}</p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span
                          className={`text-lg font-bold tabular-nums ${
                            balanceCents > 0 ? 'text-emerald-600' : 'text-muted-foreground'
                          }`}
                        >
                          {balanceCents > 0 ? '+' : ''}
                          {formatAmount(balanceCents, entry.balance?.currency ?? 'EUR')}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleOpenGrant(entry.enrollmentId, label || `Vertrag #${entry.enrollmentId}`)}
                          className="gap-1.5"
                        >
                          <Plus className="h-3.5 w-3.5" /> Gutschreiben
                        </Button>
                        {onTerminate && !BEENDETE_STATUS.has(String(entry.status || '')) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onTerminate(entry.enrollmentId, label || `Vertrag #${entry.enrollmentId}`)}
                            className="gap-1.5"
                          >
                            <CalendarX className="h-3.5 w-3.5" /> Kündigen
                          </Button>
                        )}
                      </div>
                    </div>
                    {sources.length > 0 && (
                      <ul className="mt-3 space-y-1">
                        {sources.map((src, idx) => (
                          <li key={idx} className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground">
                              {src.label}
                              {src.description ? <span className="ml-1 text-foreground/70">· {src.description}</span> : null}
                            </span>
                            <span className={`tabular-nums ${src.amountCents > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                              {src.amountCents > 0 ? '+' : ''}
                              {formatAmount(src.amountCents, entry.balance?.currency ?? 'EUR')}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {entry.history.length > 0 && (
                      <details className="mt-3">
                        <summary className="text-xs text-muted-foreground cursor-pointer select-none hover:text-foreground">
                          Historie ({entry.history.length})
                        </summary>
                        <ul className="mt-2 space-y-1 max-h-48 overflow-auto pr-1">
                          {entry.history.map((h) => (
                            <li key={h.id} className="flex items-start justify-between gap-3 text-xs rounded-sm bg-muted/40 px-2 py-1">
                              <div className="min-w-0">
                                <p className="font-medium">{CREDIT_SOURCE_LABEL[h.sourceType] ?? h.sourceType}</p>
                                {h.description && <p className="text-muted-foreground truncate">{h.description}</p>}
                                <p className="text-[10px] text-muted-foreground">
                                  {new Date(h.createdAt).toLocaleString('de-DE')}
                                </p>
                              </div>
                              <span className={`tabular-nums shrink-0 ${h.amountCents > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                {h.amountCents > 0 ? '+' : ''}
                                {formatAmount(h.amountCents, h.currency)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Zahlungshistorie</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Alle Stripe-Transaktionen dieses Kunden (letzte 12 Monate)
            </p>
          </div>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-md hover:bg-muted disabled:opacity-50"
          >
            <RefreshCcw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </button>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="flex items-center gap-2 p-3 mb-3 rounded-md bg-destructive/10 text-destructive text-sm">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}
          {!loading && rows.length === 0 && !error && (
            <p className="text-sm text-muted-foreground py-6 text-center">
              Keine Zahlungen für diesen Kunden.
            </p>
          )}
          <GrantCreditModal
            open={grantModal !== null}
            onOpenChange={(open) => { if (!open) setGrantModal(null); }}
            enrollmentId={grantModal?.enrollmentId ?? 0}
            enrollmentLabel={grantModal?.label ?? ''}
            onSuccess={() => {
              void loadCredits();
            }}
          />
          {rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="py-2 pr-3 font-medium">Status</th>
                    <th className="py-2 pr-3 font-medium">Typ</th>
                    <th className="py-2 pr-3 font-medium text-right">Betrag</th>
                    <th className="py-2 pr-3 font-medium">Datum</th>
                    <th className="py-2 pr-3 font-medium">Anmeldung</th>
                    <th className="py-2 font-medium">PaymentIntent</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const typeLabel = r.package_name
                      ? (r.portal_purchase_status ? 'Nachbuchung' : 'Initial')
                      : (r.payment_type === 'installment' ? 'Rate' : 'Erstbelastung');
                    const stripeLink = `https://dashboard.stripe.com/payments/${r.payment_intent_id}`;
                    return (
                      <tr key={r.payment_intent_id} className="border-b last:border-b-0 hover:bg-muted/40">
                        <td className="py-3 pr-3">
                          <Badge variant={statusVariant(r.current_status)}>
                            {STATUS_LABEL[r.current_status] ?? r.current_status}
                          </Badge>
                        </td>
                        <td className="py-3 pr-3 text-xs text-muted-foreground">
                          {typeLabel}
                          {r.package_name && <span className="ml-1 text-foreground">· {r.package_name}</span>}
                        </td>
                        <td className="py-3 pr-3 text-right font-medium tabular-nums">
                          {formatAmount(r.amount_cents, r.currency)}
                        </td>
                        <td className="py-3 pr-3 text-xs">{formatDate(r.last_event_created_at)}</td>
                        <td className="py-3 pr-3 text-xs">{r.enrollment_id ? `#${r.enrollment_id}` : '–'}</td>
                        <td className="py-3">
                          <a
                            href={stripeLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 font-mono text-xs text-muted-foreground hover:text-primary hover:underline"
                            title={r.payment_intent_id}
                          >
                            {r.payment_intent_id.slice(0, 18)}… <ExternalLink className="h-3 w-3" />
                          </a>
                          {r.last_error && (
                            <div className="mt-1 text-xs text-destructive max-w-[280px] truncate" title={r.last_error}>
                              {r.last_error}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  if (!visible) return null;
  if (embedded) return content;
  return (
    <TabsContent value="payments" className="space-y-4">
      {content}
    </TabsContent>
  );
}
